import * as PIXI from 'pixi.js';
import { Logger } from '../utils/Logger';

/**
 * ResourceManager class for handling game assets including audio
 */
export class ResourceManager {
  private static instance: ResourceManager | null = null;
  private pixiApp: PIXI.Application;
  private assetsDirectory: string = '';
  private logger: Logger;

  // Audio caching
  private soundCache: Map<string, HTMLAudioElement>;
  private musicCache: Map<string, HTMLAudioElement>;

  private constructor(pixiApp: PIXI.Application) {
    this.pixiApp = pixiApp;
    this.logger = Logger.getInstance();
    this.soundCache = new Map();
    this.musicCache = new Map();
  }

  /**
   * Get the ResourceManager instance
   */
  public static getInstance(pixiApp: PIXI.Application): ResourceManager {
    if (!ResourceManager.instance) {
      ResourceManager.instance = new ResourceManager(pixiApp);
    }
    return ResourceManager.instance;
  }

  /**
   * Set the assets directory
   */
  public setAssetsDirectory(directory: string): void {
    this.assetsDirectory = directory;
    this.logger.info(`Assets directory set to: ${directory}`);
  }

  /**
   * Load a texture from the assets directory
   */
  public async loadTexture(name: string): Promise<PIXI.Texture> {
    const url = `${this.assetsDirectory}/${name}`;
    try {
      const texture = await PIXI.Assets.load(url);
      this.logger.info(`Loaded texture: ${name}`);
      return texture;
    } catch (error) {
      this.logger.error(`Failed to load texture: ${name} - ${error}`);
      throw error;
    }
  }

  /**
   * Load multiple textures from the assets directory
   */
  public async loadTextures(
    names: string[]
  ): Promise<Map<string, PIXI.Texture>> {
    const textures = new Map<string, PIXI.Texture>();
    for (const name of names) {
      try {
        const texture = await this.loadTexture(name);
        textures.set(name, texture);
      } catch (error) {
        this.logger.error(`Failed to load texture: ${name} - ${error}`);
      }
    }
    return textures;
  }

  /**
   * Load a sound effect from the sounds directory.
   * Returns cached audio element if already loaded.
   */
  public loadSound(soundName: string): HTMLAudioElement | null {
    // Return cached sound if available
    if (this.soundCache.has(soundName)) {
      return this.soundCache.get(soundName)!;
    }

    try {
      const path = `${this.assetsDirectory}/sounds/${soundName}`;
      this.logger.info(`ResourceManager: Loading sound from ${path}`);

      const audio = new Audio();
      audio.src = path;
      audio.preload = 'auto';

      // Cache the audio element
      this.soundCache.set(soundName, audio);
      this.logger.info(
        `ResourceManager: Successfully loaded sound ${soundName}`
      );
      return audio;
    } catch (e) {
      this.logger.error(
        `ResourceManager: Failed to load sound ${soundName}: ${e}`
      );
      return null;
    }
  }

  /**
   * Load background music from the music directory.
   * Returns cached audio element if already loaded.
   */
  public loadMusic(musicName: string): HTMLAudioElement | null {
    // Return cached music if available
    if (this.musicCache.has(musicName)) {
      return this.musicCache.get(musicName)!;
    }

    try {
      const path = `${this.assetsDirectory}/music/${musicName}`;
      this.logger.info(`ResourceManager: Loading music from ${path}`);

      const audio = new Audio();
      audio.src = path;
      audio.preload = 'auto';
      audio.loop = true; // Background music should loop

      // Cache the audio element
      this.musicCache.set(musicName, audio);
      this.logger.info(
        `ResourceManager: Successfully loaded music ${musicName}`
      );
      return audio;
    } catch (e) {
      this.logger.error(
        `ResourceManager: Failed to load music ${musicName}: ${e}`
      );
      return null;
    }
  }

  /**
   * Clear all cached audio resources.
   * Call this when shutting down the game to prevent memory leaks.
   */
  public clearAudioCache(): void {
    this.logger.info('ResourceManager: Clearing audio cache');

    // Stop and clear sound cache
    for (const audio of this.soundCache.values()) {
      audio.pause();
      audio.src = '';
    }
    this.soundCache.clear();

    // Stop and clear music cache
    for (const audio of this.musicCache.values()) {
      audio.pause();
      audio.src = '';
    }
    this.musicCache.clear();
  }

  /**
   * Get the assets directory
   */
  public getAssetsDirectory(): string {
    return this.assetsDirectory;
  }
}
